import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";
import type { IdentityBackfillJobRepository } from "./identity_backfill_job_bloc";
import type { JobRecord, JobStatus } from "./types";

const client = generateClient<Schema>();

const JOB_TYPE = "identity-backfill-dedupe";

function toJobRecord(raw: Schema["Job"]["type"]): JobRecord {
  return {
    id: raw.id,
    type: raw.type,
    status: (raw.status ?? "pending") as JobStatus,
    triggeredBy: raw.triggeredBy,
    total: raw.total ?? null,
    processed: raw.processed ?? 0,
    counters:
      typeof raw.counters === "string"
        ? raw.counters
        : raw.counters != null
          ? JSON.stringify(raw.counters)
          : null,
    result:
      typeof raw.result === "string"
        ? raw.result
        : raw.result != null
          ? JSON.stringify(raw.result)
          : null,
    error: raw.error ?? null,
    startedAt: raw.startedAt ?? null,
    completedAt: raw.completedAt ?? null,
  };
}

export const identityBackfillJobRepository: IdentityBackfillJobRepository = {
  async kickoff(options) {
    const input = options?.allowFromUnrecoverable
      ? { allowFromUnrecoverable: true }
      : {};
    const result = await client.mutations.enqueueJob({
      type: JOB_TYPE,
      input: JSON.stringify(input),
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    if (!result.data?.jobId) throw new Error("No jobId returned from mutation");
    return result.data.jobId;
  },

  async findRunningJob() {
    const result = await client.models.Job.list({
      filter: {
        type: { eq: JOB_TYPE },
        status: { eq: "running" },
      },
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
    const first = result.data[0];
    return first ? toJobRecord(first) : null;
  },

  async cancelJob(jobId) {
    const current = await client.models.Job.get({ id: jobId });
    if (current.errors?.length) throw new Error(current.errors[0].message);
    const status = current.data?.status;
    if (status !== "running" && status !== "pending") return;
    const result = await client.models.Job.update({
      id: jobId,
      status: "cancelled",
    });
    if (result.errors?.length) throw new Error(result.errors[0].message);
  },

  subscribeToJob(jobId, onUpdate, onError) {
    const sub = client.models.Job.onUpdate({
      filter: { id: { eq: jobId } },
    }).subscribe({
      next: (data) => {
        onUpdate(toJobRecord(data));
      },
      error: (err: unknown) => {
        const error = err instanceof Error ? err : new Error(String(err));
        onError(error);
      },
    });

    return () => sub.unsubscribe();
  },
};
